// @ts-nocheck
import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Drawer, Switch, Dropdown, Progress, message,
} from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, CloseOutlined,
  TagsOutlined, CheckCircleOutlined, ClockCircleOutlined, RiseOutlined, SearchOutlined,
  TeamOutlined, ContainerOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { Voucher } from "@kawkaw/shared";
import dayjs from "dayjs";
import { DataTable } from "../components/DataTable";
import { ImageUpload } from "../components/ImageUpload";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
const { color: T } = tokens;
import { useAdminVouchers, useCreateVoucher, useUpdateVoucher, useDeleteVoucher, useDistributeVoucher, useAdminCustomers, useAdminStalls, useVoucherUsage } from "../api/hooks";
import { formatMonthDay } from "../utils/formatLocaleDate";

/* ─── Styling maps ───────────────────────────────────────── */
const TYPE_TAG_STYLE: Record<string, { color: string; bg: string }> = {
  percentage: { color: T.siderHover, bg: T.surfaceSection },
  fixed:      { color: T.infoDeep, bg: T.infoBg },
  free_item:  { color: T.successText, bg: T.successBg },
};

const STATUS_PILL: Record<string, { color: string; bg: string }> = {
  active:    { color: T.successText, bg: T.successBg },
  scheduled: { color: T.infoDeep, bg: T.infoBg },
  expired:   { color: T.textSecondary, bg: T.surfaceSection },
  disabled:  { color: T.dangerText, bg: T.dangerBg },
};

/* ─── Stat card ──────────────────────────────────────────── */
function StatCard({
  icon, iconColor, badge, badgeColor, badgeBg, label, value,
}: {
  icon: ReactNode; iconColor: string;
  badge: string; badgeColor: string; badgeBg: string;
  label: string; value: number | string;
}) {
  return (
    <div className="admin-kpi-card admin-voucher-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="admin-voucher-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

/* ─── Helper sub-components ─────────────────────────────── */
function DiscountTypeTabs({ value, onChange, tabs }: { value: string; onChange: (v: string) => void; tabs: { value: string; label: string }[] }) {
  return (
    <div className="admin-voucher-tabs" style={{ display: "flex", gap: 4, background: T.surfaceSection, borderRadius: 10, padding: 4, marginBottom: 16 }}>
      {tabs.map(tab => (
        <button key={tab.value} type="button" onClick={() => onChange(tab.value)} style={{
          flex: 1, padding: "8px 4px", borderRadius: 8, border: "none", cursor: "pointer",
          background: value === tab.value ? T.brand : "transparent",
          color: value === tab.value ? T.surface : T.textBody,
          fontSize: 13, fontWeight: 700, transition: "all .15s",
        }}>{tab.label}</button>
      ))}
    </div>
  );
}

function ApplicableTabs({ value, onChange, tabs }: { value: string; onChange: (v: string) => void; tabs: { value: string; label: string; icon: string }[] }) {
  return (
    <div className="admin-voucher-tabs" style={{ display: "flex", gap: 4, background: T.surfaceSection, borderRadius: 10, padding: 4 }}>
      {tabs.map(tab => (
        <button key={tab.value} type="button" onClick={() => onChange(tab.value)} style={{
          flex: 1, padding: "8px 4px", borderRadius: 8, border: "none", cursor: "pointer",
          background: value === tab.value ? T.brand : "transparent",
          color: value === tab.value ? T.surface : T.textBody,
          fontSize: 13, fontWeight: 700, transition: "all .15s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <span>{tab.icon}</span><span>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="admin-voucher-section-label" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, marginTop: 24 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand, flexShrink: 0, display: "inline-block" }} />
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.brand, textTransform: "uppercase" as const }}>{children}</span>
    </div>
  );
}

const FL = (text: string) => (
  <span className="admin-voucher-field-label" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: T.textMuted, textTransform: "uppercase" as const }}>{text}</span>
);

const genCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

/* ─── Screen ─────────────────────────────────────────────── */
export function VouchersScreen() {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Voucher | null>(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [discountType, setDiscountType] = useState("percentage");
  const [applicableTo, setApplicableTo] = useState("all");
  const [enableImmediately, setEnableImmediately] = useState(true);
  const [voucherScope, setVoucherScope] = useState<"platform" | "merchant">("platform");
  const [discountTarget, setDiscountTarget] = useState<"subtotal" | "delivery_fee">("subtotal");
  const [voucherImageUrl, setVoucherImageUrl] = useState<string | null>(null);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [distributeTarget, setDistributeTarget] = useState<Voucher | null>(null);
  const [distributeSearch, setDistributeSearch] = useState("");
  const [distributeSelected, setDistributeSelected] = useState<string[]>([]);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageTarget, setUsageTarget] = useState<Voucher | null>(null);

  const discountTabs = useMemo(
    () => [
      { value: "percentage", label: t("vouchers.tabPercentage") },
      { value: "fixed", label: t("vouchers.tabFixedRm") },
      { value: "free_item", label: t("vouchers.tabFreeDelivery") },
    ],
    [t],
  );
  const applicableTabs = useMemo(
    () => [
      { value: "all", label: t("vouchers.tabApplicableAll"), icon: "≡" },
      { value: "pickup", label: t("vouchers.tabApplicablePickup"), icon: "🛒" },
      { value: "delivery", label: t("vouchers.tabApplicableDelivery"), icon: "🚚" },
    ],
    [t],
  );

  const voucherStatusLabel = (s: string) => {
    const m: Record<string, string> = {
      active: t("vouchers.filterStatusActive"),
      scheduled: t("vouchers.filterStatusScheduled"),
      expired: t("vouchers.filterStatusExpired"),
      disabled: t("vouchers.filterStatusDisabled"),
    };
    return m[s] ?? s;
  };

  const typeTagLabel = (typ: string) => {
    const m: Record<string, string> = {
      percentage: t("vouchers.typeTagPercentage"),
      fixed: t("vouchers.typeTagFixed"),
      free_item: t("vouchers.typeTagFreeItem"),
    };
    return m[typ] ?? typ.replace(/_/g, " ").toUpperCase();
  };

  const { data, isLoading } = useAdminVouchers();
  const createVoucher = useCreateVoucher();
  const updateVoucher = useUpdateVoucher();
  const deleteVoucher = useDeleteVoucher();
  const distributeVoucher = useDistributeVoucher();
  const { data: stallsData } = useAdminStalls();
  const { data: customersData } = useAdminCustomers(distributeSearch);
  const stalls = (stallsData?.data ?? []) as unknown as Array<{ id: string; name: string }>;
  const customerResults = customersData?.data ?? [];
  const { data: usageData, isLoading: usageLoading } = useVoucherUsage(usageTarget?.id ?? null);
  const usageRows = usageData?.data?.orders ?? [];

  const vouchers = (data?.data ?? []) as unknown as Voucher[];

  /* Stats */
  const totalVouchers = vouchers.length;
  const activeCount   = vouchers.filter(v => v.status === "active").length;
  const expiredCount  = vouchers.filter(v => v.status === "expired").length;
  const totalRedeemed = vouchers.reduce((s, v) => s + (v.used_count ?? 0), 0);

  /* Filtered rows */
  const filtered = useMemo(() => {
    let rows = vouchers;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(v =>
        v.code.toLowerCase().includes(q) ||
        (v.name ?? "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") rows = rows.filter(v => v.status === statusFilter);
    if (typeFilter   !== "all") rows = rows.filter(v => v.type   === typeFilter);
    return rows;
  }, [vouchers, search, statusFilter, typeFilter]);

  /* Handlers */
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDiscountType("percentage");
    setApplicableTo("all");
    setEnableImmediately(true);
    setVoucherScope("platform");
    setDiscountTarget("subtotal");
    setVoucherImageUrl(null);
    setModalOpen(true);
  };
  const openEdit = (v: Voucher) => {
    setEditing(v);
    setDiscountType(v.type ?? "percentage");
    setApplicableTo("all");
    setEnableImmediately(v.status === "active");
    setVoucherScope((v as unknown as { scope?: "platform" | "merchant" }).scope ?? "platform");
    setDiscountTarget((v as unknown as { discount_target?: "subtotal" | "delivery_fee" }).discount_target ?? "subtotal");
    setVoucherImageUrl(v.image_url ?? null);
    form.setFieldsValue({
      ...v,
      discount_type:  v.type,
      discount_value: v.value,
      min_spend:      v.min_order,
      max_cap:        (v as unknown as { max_discount?: string | null }).max_discount != null ? Number((v as unknown as { max_discount: string }).max_discount) : undefined,
      usage_limit:    v.total_uses,
      valid_from:  v.valid_from ? dayjs(v.valid_from) : null,
      valid_until: v.valid_to   ? dayjs(v.valid_to)   : null,
      stall_id: (v as unknown as { stall_id?: string }).stall_id,
      no_minimum: (v as unknown as { no_minimum?: boolean }).no_minimum ?? false,
      daily_limit_per_customer: (v as unknown as { daily_limit_per_customer?: number | null }).daily_limit_per_customer,
    });
    setModalOpen(true);
  };

  const openUsage = (v: Voucher) => {
    setUsageTarget(v);
    setUsageOpen(true);
  };

  const openDistribute = (v: Voucher) => {
    setDistributeTarget(v);
    setDistributeSelected([]);
    setDistributeSearch("");
    setDistributeOpen(true);
  };
  const submitDistribute = async () => {
    if (!distributeTarget || distributeSelected.length === 0) return;
    try {
      const res = await distributeVoucher.mutateAsync({ id: distributeTarget.id, customerIds: distributeSelected });
      void message.success(t("vouchers.distributionSuccess", { count: res.data.distributed }));
      setDistributeOpen(false);
    } catch {
      void message.error(t("vouchers.saveFailed"));
    }
  };

  const confirmDelete = (row: Voucher) => {
    Modal.confirm({
      title:      t("vouchers.deleteConfirm"),
      content:    t("vouchers.deleteConfirmDesc"),
      okText:     t("common.yes"),
      cancelText: t("common.no"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteVoucher.mutateAsync(row.id);
          void message.success(t("vouchers.deleteSuccess"));
        } catch {
          void message.error(t("vouchers.deleteFailed"));
        }
      },
    });
  };

  const onSave = async (values: Record<string, unknown>) => {
    const payload = {
      ...values,
      discount_type: discountType,
      applicable_to: applicableTo,
      status: enableImmediately ? "active" : "scheduled",
      valid_from:  (values.valid_from  as dayjs.Dayjs | undefined)?.toISOString() ?? null,
      valid_until: (values.valid_until as dayjs.Dayjs | undefined)?.toISOString() ?? null,
      scope: voucherScope,
      stall_id: voucherScope === "merchant" ? (values.stall_id as string | undefined) : null,
      discount_target: discountTarget,
      image_url: voucherImageUrl,
    };
    try {
      if (editing) {
        await updateVoucher.mutateAsync({ id: editing.id, ...payload });
        void message.success(t("vouchers.updateSuccess"));
      } else {
        await createVoucher.mutateAsync(payload);
        void message.success(t("vouchers.createSuccess"));
      }
      setModalOpen(false);
    } catch {
      void message.error(t("vouchers.saveFailed"));
    }
  };

  /* Columns */
  const columns: ColumnsType<Voucher> = [
    {
      title: t("vouchers.colCode"), dataIndex: "code", key: "code", width: 150,
      render: (v) => (
        <span className="admin-data-code">{v}</span>
      ),
    },
    {
      title: t("vouchers.colVoucherName"), key: "vname", width: 220,
      render: (_, row) => {
        const imageUrl = row.image_url;
        return (
          <div className="admin-voucher-name-cell" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
              />
            )}
            <div>
              <div className="admin-data-customer">{row.name}</div>
              {row.description && (
                <div className="admin-data-secondary">{row.description}</div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t("vouchers.colType"), dataIndex: "type", key: "type", width: 160,
      render: (v: string) => {
        const s = TYPE_TAG_STYLE[v] ?? { color: T.siderHover, bg: T.surfaceSection };
        return (
          <span className="admin-data-badge inline-block" style={{ background: s.bg, color: s.color }}>
            {typeTagLabel(v)}
          </span>
        );
      },
    },
    {
      title: t("vouchers.colValue"), key: "value", width: 120, align: "right",
      render: (_, row) => {
        if (row.type === "percentage")
          return <span className="admin-data-amount">{row.value}%</span>;
        if (row.type === "free_item")
          return <span style={{ color: T.textMuted }}>—</span>;
        return (
          <span className="admin-data-amount">
            RM {parseFloat(String(row.value)).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: t("vouchers.colMinOrder"), dataIndex: "min_order", key: "min", width: 140, align: "right",
      render: (v) => {
        const num = parseFloat(String(v));
        return num > 0
          ? <span className="admin-data-amount">RM {num.toFixed(2)}</span>
          : <span style={{ color: T.textMuted }}>—</span>;
      },
    },
    {
      title: t("vouchers.colUsage"), key: "usage", width: 170,
      render: (_, row) => {
        const used  = row.used_count ?? 0;
        const total = row.total_uses ?? null;
        const pct   = total && total > 0 ? Math.round((used / total) * 100) : 0;
        return (
          <div className="admin-voucher-usage-meter" style={{ minWidth: 110 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ color: T.textDark, fontWeight: 600 }}>{used}/{total ?? "∞"}</span>
              {total !== null && (
                <span style={{ color: T.textMuted, fontSize: 11 }}>{pct}%</span>
              )}
            </div>
            {total !== null && (
              <Progress
                percent={pct} size="small" showInfo={false}
                strokeColor={T.brand} trailColor={T.border}
                style={{ marginTop: 4, marginBottom: 0 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: t("vouchers.colValidPeriod"), key: "period", width: 180,
      render: (_, row) => {
        const from = row.valid_from ? formatMonthDay(row.valid_from, i18n.language) : null;
        const to   = row.valid_to   ? formatMonthDay(row.valid_to, i18n.language)   : null;
        if (!from && !to) return <span style={{ color: T.textMuted }}>{t("vouchers.ongoing")}</span>;
        return <span className="admin-data-time">{from ?? "—"} – {to ?? "∞"}</span>;
      },
    },
    {
      title: t("vouchers.colStatusHeader"), dataIndex: "status", key: "status", width: 140,
      render: (v: string) => {
        const s = STATUS_PILL[v] ?? { color: T.siderHover, bg: T.surfaceSection };
        return (
          <span className="admin-data-badge inline-block" style={{ background: s.bg, color: s.color }}>
            {voucherStatusLabel(v)}
          </span>
        );
      },
    },
    {
      title: t("vouchers.colActions"), key: "actions", width: 96, fixed: "right",
      render: (_, row) => (
        <Dropdown
          menu={{
            items: [
              { key: "edit",       icon: <EditOutlined />,   label: t("vouchers.menuEdit"), onClick: () => openEdit(row) },
              { key: "usage",      icon: <ContainerOutlined />, label: t("vouchers.menuUsage"), onClick: () => openUsage(row) },
              { key: "distribute", icon: <TeamOutlined />,   label: t("vouchers.menuDistribute"), onClick: () => openDistribute(row) },
              { key: "delete",     icon: <DeleteOutlined />, label: t("vouchers.menuDelete"), danger: true, onClick: () => confirmDelete(row) },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button className="admin-voucher-more admin-data-more" type="text" aria-label={t("orders.colAction")} icon={<MoreOutlined style={{ fontSize: 18 }} />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-vouchers-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
      {/* ── Page header ── */}
      <div className="admin-vouchers-toolbar">
        <div>
          <h1 className="admin-page-title">{t("vouchers.title")}</h1>
          <p className="admin-vouchers-subtitle">{t("vouchers.pageSubtitle")}</p>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />} onClick={openCreate}
          className="admin-vouchers-create"
        >
          {t("vouchers.pageCreateBtn")}
        </Button>
      </div>

      {/* ── Stat cards ── */}
      <div className="admin-vouchers-stats admin-txn-stats grid">
        <StatCard
          icon={<TagsOutlined />}         iconColor={T.brand}
          badge={t("vouchers.statBadgeOverall")}  badgeColor={T.brandAmberText}  badgeBg={T.warningBg}
          label={t("vouchers.statTotalVouchers")} value={totalVouchers}
        />
        <StatCard
          icon={<CheckCircleOutlined />}  iconColor={T.successBright}
          badge={t("vouchers.statBadgeActive")}   badgeColor={T.successText}  badgeBg={T.successBg}
          label={t("vouchers.statActive")} value={activeCount}
        />
        <StatCard
          icon={<ClockCircleOutlined />}  iconColor={T.dangerText}
          badge={t("vouchers.statBadgeInactive")} badgeColor={T.dangerText}  badgeBg={T.dangerBg}
          label={t("vouchers.statExpired")} value={expiredCount}
        />
        <StatCard
          icon={<RiseOutlined />}         iconColor={T.brand}
          badge={t("vouchers.statBadgeGrowth")}   badgeColor={T.brandAmberText}  badgeBg={T.warningBg}
          label={t("vouchers.statTotalRedeemed")} value={totalRedeemed}
        />
      </div>

      {/* ── Filter bar ── */}
      <div className="admin-vouchers-filters admin-txn-filter-panel">
        <Input
          prefix={<SearchOutlined style={{ color: T.textMuted }} />}
          placeholder={t("vouchers.searchPlaceholder")}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1, height: 40, borderRadius: 10 }}
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          className="w-full sm:w-[140px]"
          style={{ height: 40 }}
          options={[
            { value: "all",       label: t("vouchers.filterAllStatus") },
            { value: "active",    label: t("vouchers.filterStatusActive") },
            { value: "scheduled", label: t("vouchers.filterStatusScheduled") },
            { value: "expired",   label: t("vouchers.filterStatusExpired") },
            { value: "disabled",  label: t("vouchers.filterStatusDisabled") },
          ]}
        />
        <Select
          value={typeFilter}
          onChange={setTypeFilter}
          className="w-full sm:w-[140px]"
          style={{ height: 40 }}
          options={[
            { value: "all",        label: t("vouchers.filterAllTypes") },
            { value: "percentage", label: t("vouchers.filterTypePercentage") },
            { value: "fixed",      label: t("vouchers.filterTypeFixed") },
            { value: "free_item",  label: t("vouchers.filterTypeFreeItem") },
          ]}
        />
      </div>

      {/* ── Table ── */}
      <p className="admin-vouchers-results">{t("common.total", { count: filtered.length })}</p>
      <div className="admin-vouchers-table-panel admin-data-table-panel">
        <DataTable<Voucher>
          columns={columns}
          dataSource={filtered as unknown as Voucher[]}
          loading={isLoading}
        />
      </div>
        </div>
      </div>

      {/* ── Create / Edit Drawer ── */}
      <Drawer
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        width={460}
        closeIcon={null}
        title={
          <div className="admin-vouchers-drawer-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="admin-vouchers-drawer-title" style={{ fontSize: 20, fontWeight: 800, color: T.textDark, lineHeight: 1.2 }}>
                  {editing ? t("vouchers.editTitle") : t("vouchers.pageCreateBtn")}
                </span>
                {editing && (() => {
                  const statusKey = editing.status ?? "active";
                  const s = STATUS_PILL[statusKey] ?? { color: T.siderHover, bg: T.surfaceSection };
                  return (
                    <span style={{
                      fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
                      padding: "3px 10px", borderRadius: 100,
                      background: s.bg, color: s.color, textTransform: "uppercase",
                    }}>
                      {voucherStatusLabel(statusKey)}
                    </span>
                  );
                })()}
              </div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4, fontWeight: 400 }}>
                {editing ? editing.code : t("vouchers.drawerSubtitleNew")}
              </div>
            </div>
            <Button className="admin-duplicate-close" type="text" icon={<CloseOutlined />} onClick={() => setModalOpen(false)}
              style={{ color: T.textBody, marginTop: -4, marginRight: -8 }} />
          </div>
        }
        footer={
          <div style={{ display: "flex", gap: 12, padding: "4px 0" }}>
            <Button
              style={{ flex: 1, height: 48, borderRadius: 10, fontWeight: 700, fontSize: 14 }}
              onClick={() => setModalOpen(false)}
            >
              {t("vouchers.btnCancel")}
            </Button>
            <Button
              type="primary"
              style={{ flex: 2, height: 48, borderRadius: 10, fontWeight: 700, fontSize: 14, background: T.brand, borderColor: T.brand }}
              loading={createVoucher.isPending || updateVoucher.isPending}
              onClick={() => void form.submit()}
            >
              {editing ? t("vouchers.btnSaveChanges") : t("vouchers.btnSaveVoucher")}
            </Button>
          </div>
        }
        styles={{ footer: { padding: "16px 24px", borderTop: "1px solid #F3F4F6" } }}
        maskClosable={false}
        destroyOnClose
      >
        <div className="admin-vouchers-form">
        <Form form={form} layout="vertical" onFinish={onSave} colon={false}>

          {/* ── BASIC INFORMATION ── */}
          <SectionLabel>{t("vouchers.sectionBasic")}</SectionLabel>

          <Form.Item name="name" label={FL(t("vouchers.lblVoucherName"))} style={{ marginBottom: 14 }}>
            <Input
              placeholder={t("vouchers.phVoucherName")}
              style={{ height: 44, borderRadius: 10, background: T.bg }}
            />
          </Form.Item>

          <Form.Item label={FL(t("vouchers.lblVoucherCode"))} required style={{ marginBottom: 14 }}>
            <div className="admin-voucher-code-field" style={{ display: "flex", gap: 8 }}>
              <Form.Item name="code" rules={[{ required: true, message: t("vouchers.codeRequired") }]} noStyle>
                <Input
                  style={{ flex: 1, height: 44, borderRadius: 10, fontFamily: "monospace", fontWeight: 700, background: T.bg }}
                  onChange={e => form.setFieldValue("code", e.target.value.toUpperCase())}
                />
              </Form.Item>
              <Button
                onClick={() => form.setFieldValue("code", genCode())}
                style={{ height: 44, borderRadius: 10, background: T.brandLight, border: "1px solid #F97316", color: T.brand, fontWeight: 700, flexShrink: 0, fontSize: 12 }}
              >
                {t("vouchers.btnAutoGenerate")}
              </Button>
            </div>
          </Form.Item>

          <Form.Item name="description" label={FL(t("vouchers.lblDescription"))} style={{ marginBottom: 8 }}>
            <Input.TextArea rows={3} style={{ borderRadius: 10, background: T.bg, resize: "none" }} />
          </Form.Item>

          <Form.Item label={FL(t("vouchers.lblVoucherImage"))} style={{ marginBottom: 8 }}>
            <ImageUpload value={voucherImageUrl ?? undefined} onChange={setVoucherImageUrl} folder="vouchers" />
          </Form.Item>

          {/* ── DISCOUNT SETTINGS ── */}
          <SectionLabel>{t("vouchers.sectionDiscount")}</SectionLabel>

          <DiscountTypeTabs value={discountType} onChange={v => setDiscountType(v)} tabs={discountTabs} />

          {/* Percentage: value + max cap side-by-side */}
          {discountType === "percentage" && (
            <div className="admin-voucher-fields" style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <Form.Item
                name="discount_value"
                label={FL(t("vouchers.lblValuePct"))}
                rules={[{ required: true }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <InputNumber min={0} max={100} precision={2} placeholder="15" style={{ width: "100%", height: 44, borderRadius: 10 }} />
              </Form.Item>
              <Form.Item name="max_cap" label={FL(t("vouchers.lblMaxCapRm"))} style={{ flex: 1, marginBottom: 0 }}>
                <InputNumber min={0} precision={2} placeholder="10.00" style={{ width: "100%", height: 44, borderRadius: 10 }} />
              </Form.Item>
            </div>
          )}

          {/* Fixed RM: single full-width discount amount */}
          {discountType === "fixed" && (
            <Form.Item
              name="discount_value"
              label={FL(t("vouchers.lblDiscountAmountRm"))}
              rules={[{ required: true }]}
              style={{ marginBottom: 14 }}
            >
              <InputNumber min={0} precision={2} placeholder="5.00" style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
          )}

          {/* Free Delivery: info text only, no amount input */}
          {discountType === "free_item" && (
            <div style={{ fontSize: 13, color: T.textBody, marginBottom: 14, padding: "10px 0" }}>
              {t("vouchers.freeDeliveryHint")}
            </div>
          )}

          {/* Min order — all tabs */}
          <Form.Item name="min_spend" label={FL(t("vouchers.lblMinOrderRm"))} style={{ marginBottom: 8 }}>
            <InputNumber min={0} precision={2} placeholder="25.00" style={{ width: "100%", height: 44, borderRadius: 10 }} />
          </Form.Item>

          {/* ── DISTRIBUTION & STACKING (scope / discount target / no-minimum) ── */}
          <SectionLabel>{t("vouchers.sectionDistribution")}</SectionLabel>
          <div className="admin-voucher-fields" style={{ display: "flex", gap: 12, marginBottom: 8 }}>
            <Form.Item label={FL(t("vouchers.lblScope"))} style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={voucherScope}
                onChange={(v: "platform" | "merchant") => setVoucherScope(v)}
                style={{ width: "100%", height: 44 }}
                options={[
                  { value: "platform", label: t("vouchers.scopePlatform") },
                  { value: "merchant", label: t("vouchers.scopeMerchant") },
                ]}
              />
            </Form.Item>
            <Form.Item label={FL(t("vouchers.lblDiscountTarget"))} style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={discountTarget}
                onChange={(v: "subtotal" | "delivery_fee") => setDiscountTarget(v)}
                style={{ width: "100%", height: 44 }}
                options={[
                  { value: "subtotal", label: t("vouchers.targetSubtotal") },
                  { value: "delivery_fee", label: t("vouchers.targetDeliveryFee") },
                ]}
              />
            </Form.Item>
          </div>
          {voucherScope === "merchant" && (
            <Form.Item name="stall_id" label={FL(t("vouchers.lblMerchantStall"))} style={{ marginBottom: 8 }}
              rules={[{ required: true, message: t("vouchers.merchantStallRequired") }]}>
              <Select
                placeholder={t("vouchers.phMerchantStall")}
                style={{ width: "100%", height: 44 }}
                options={stalls.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          )}
          <div className="admin-voucher-fields" style={{ display: "flex", gap: 12, marginBottom: 8 }}>
            <Form.Item name="no_minimum" label={FL(t("vouchers.lblNoMinimum"))} valuePropName="checked" style={{ flex: 1, marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item name="daily_limit_per_customer" label={FL(t("vouchers.lblDailyUseCap"))} style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber min={1} placeholder={t("vouchers.phUnlimited")} style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
            {t("vouchers.stackingHint")}
          </div>

          {/* ── AUTO-GRANT ── */}
          <SectionLabel>{t("vouchers.sectionAutoGrant")}</SectionLabel>
          <Form.Item name="auto_grant_trigger" label={FL(t("vouchers.lblAutoGrantTrigger"))} style={{ marginBottom: 8 }}>
            <Select
              allowClear
              placeholder={t("vouchers.autoGrantNone")}
              style={{ width: "100%", height: 44 }}
              options={[
                { value: "first_order", label: t("vouchers.autoGrantFirstOrder") },
              ]}
            />
          </Form.Item>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
            {t("vouchers.autoGrantHint")}
          </div>

          {/* ── USAGE LIMITS ── */}
          <SectionLabel>{t("vouchers.sectionUsage")}</SectionLabel>

          <div className="admin-voucher-fields" style={{ display: "flex", gap: 12, marginBottom: 8 }}>
            <Form.Item name="usage_limit" label={FL(t("vouchers.lblTotalUsageLimit"))} style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber min={1} placeholder="100" style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
            <Form.Item name="per_customer" label={FL(t("vouchers.lblPerCustomer"))} style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber min={1} placeholder="1" style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
          </div>

          {/* ── VALIDITY PERIOD ── */}
          <SectionLabel>{t("vouchers.sectionValidity")}</SectionLabel>

          <div className="admin-voucher-fields" style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <Form.Item name="valid_from" label={FL(t("vouchers.lblStartDate"))} style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
            <Form.Item name="valid_until" label={FL(t("vouchers.lblEndDate"))} style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker style={{ width: "100%", height: 44, borderRadius: 10 }} />
            </Form.Item>
          </div>

          <div className="admin-voucher-enable" style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: T.bg, border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 16px",
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{t("vouchers.enableImmediatelyTitle")}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, letterSpacing: 0.3 }}>
                {t("vouchers.enableImmediatelyHint")}
              </div>
            </div>
            <Switch
              checked={enableImmediately}
              onChange={setEnableImmediately}
              style={{ background: enableImmediately ? T.successBright : undefined }}
            />
          </div>

          {/* ── APPLICABLE TO ── */}
          <SectionLabel>{t("vouchers.sectionApplicable")}</SectionLabel>
          <ApplicableTabs value={applicableTo} onChange={setApplicableTo} tabs={applicableTabs} />

        </Form>
        </div>
      </Drawer>

      {/* ── Distribute to customers ── */}
      <Modal
        open={distributeOpen}
        onCancel={() => setDistributeOpen(false)}
        title={<h2 className="admin-voucher-dialog-title">{t("vouchers.distributeTitle", { code: distributeTarget?.code ?? "" })}</h2>}
        onOk={() => void submitDistribute()}
        okText={t("vouchers.sendToWallet")}
        confirmLoading={distributeVoucher.isPending}
        okButtonProps={{ disabled: distributeSelected.length === 0 }}
      >
        <div className="admin-voucher-distribute">
          <div className="admin-voucher-dialog-hint">
            {t("vouchers.distributeHint")}
          </div>
          <Input
            placeholder={t("vouchers.distributeSearch")}
            value={distributeSearch}
            onChange={(e) => setDistributeSearch(e.target.value)}
            style={{ marginBottom: 10, height: 40, borderRadius: 10 }}
          />
          <Select
            mode="multiple"
            value={distributeSelected}
            onChange={setDistributeSelected}
            style={{ width: "100%" }}
            placeholder={t("vouchers.distributeSelect")}
            options={customerResults.map((c) => ({ value: c.id, label: `${c.name ?? t("transactions.customerLabel")} — ${c.phone}` }))}
          />
        </div>
      </Modal>

      {/* ── Usage log: every order this voucher was actually redeemed on ── */}
      <Modal
        open={usageOpen}
        onCancel={() => setUsageOpen(false)}
        title={<h2 className="admin-voucher-dialog-title">{t("vouchers.usageTitle", { code: usageTarget?.code ?? "" })}</h2>}
        footer={null}
        width={640}
      >
        <div className="admin-voucher-usage-dialog">
          {usageLoading ? (
            <div className="admin-voucher-dialog-state">{t("common.loading")}</div>
          ) : usageRows.length === 0 ? (
            <div className="admin-voucher-dialog-state">
              {t("vouchers.usageEmpty")}
            </div>
          ) : (
            <div className="admin-voucher-usage-table admin-data-table-panel" style={{ maxHeight: 440, overflowY: "auto" }}>
              <div className="admin-table-wrap" style={{ overflowX: "auto" }}>
                <table className="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                      <th>{t("dashboard.colOrderId")}</th>
                      <th>{t("dashboard.colCustomer")}</th>
                      <th>{t("vouchers.usageSlot")}</th>
                      <th>{t("vouchers.usageDiscount")}</th>
                      <th>{t("dashboard.colStatus")}</th>
                      <th>{t("transactions.colDateTime")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map((r) => (
                      <tr key={r.orderId} style={{ borderTop: "1px solid #F3F4F6" }}>
                        <td className="admin-data-code">{r.orderCode}</td>
                        <td>{r.customer ? `${r.customer.name ?? t("transactions.customerLabel")} (${r.customer.phone})` : "—"}</td>
                        <td>{r.slot === "slot1" ? t("vouchers.slotMerchantPlatform") : r.slot === "slot2" ? t("vouchers.slotPlatform") : t("vouchers.slotDeliveryFee")}</td>
                        <td className="admin-data-amount">-RM {Number(r.discountAmount).toFixed(2)}</td>
                        <td style={{ textTransform: "capitalize" }}>{r.status}</td>
                        <td className="admin-data-secondary">{new Date(r.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </AdminShell>
  );
}
